// useHoverZoom -- Amazon-style in-place hover-zoom lens for the product
// editor Stage preview image.
//
// The Stage preview is a plain <img object-fit: contain> Artifi render
// (catalog-resolution, ~1200x1800-class for SanMar 18000). This composable
// turns a mousemove over that image into a floating loupe (the "lens")
// whose CSS background is the SAME image scaled up and offset so it shows
// the magnified region under the cursor. No canvas pixel-copy and no new
// npm dependency -- a background-position lens is the clean approach for a
// plain <img> source.
//
// The correctness-critical piece is the letterbox math: object-fit:contain
// centers the image inside the element with letterbox bars, so the rendered
// image rectangle is a sub-rect of the element box. All cursor->image
// mapping runs against that sub-rect (computeRenderedImageRect) -- using the
// raw element box would drift the lens at aspect ratios that don't match the
// container (the #1 pitfall called out in the design).
//
// The zoom factor is capped when the source render is too small to magnify
// without visible blur (effectiveZoomFactor) so the loupe never zooms into
// mush. This does NOT change the Artifi request resolution -- it only caps
// the client-side magnification.
//
// The pure helpers (computeRenderedImageRect / effectiveZoomFactor /
// computeLensState) carry all the geometry and are unit-tested directly;
// useHoverZoom wires them to Vue reactive state + DOM event handlers.

import { ref, computed, watch } from "vue";

// Default requested magnification. Amazon-style loupes sit around 2-2.5x;
// 2.2x reads as a meaningful zoom without demanding excessive source detail.
export const DEFAULT_ZOOM_FACTOR = 2.2;
// Lens diameter in CSS pixels (the loupe is a square with rounded corners).
// Bumped 160 -> 200 (+25%) for a larger reticule (Jake 2026-07-10). Single
// source: the loupe CSS width/height, clamp bounds, and centering all derive
// from this constant, so changing it here cascades everywhere.
export const DEFAULT_LENS_SIZE = 200;
// Headroom applied to the lossless zoom cap. The Artifi Stage render lands
// with naturalWidth ~= renderedWidth, so a strict lossless cap
// (naturalWidth / renderedWidth) collapses the effective zoom to ~1x and the
// loupe barely magnifies. Allowing up to 1.5x OVER native pixels (Jake's
// call) trades mild upscaling blur for a lens that actually reads as a zoom;
// where the source has real detail the requested factor still wins.
export const ZOOM_HEADROOM = 1.5;

/**
 * Compute the rectangle actually occupied by an object-fit:contain image
 * inside its element box. Returns { width, height, offsetX, offsetY } in
 * element-relative coordinates, or null when natural dimensions are
 * unavailable (image not loaded yet).
 */
export function computeRenderedImageRect({
    elementWidth,
    elementHeight,
    naturalWidth,
    naturalHeight,
}) {
    if (
        !naturalWidth ||
        !naturalHeight ||
        !elementWidth ||
        !elementHeight
    ) {
        return null;
    }
    const elementRatio = elementWidth / elementHeight;
    const imageRatio = naturalWidth / naturalHeight;
    let width;
    let height;
    if (imageRatio > elementRatio) {
        // Image is wider than the box -> constrained by width; bars top/bottom.
        width = elementWidth;
        height = elementWidth / imageRatio;
    } else {
        // Image is taller (or equal) -> constrained by height; bars left/right.
        height = elementHeight;
        width = elementHeight * imageRatio;
    }
    return {
        width,
        height,
        offsetX: (elementWidth - width) / 2,
        offsetY: (elementHeight - height) / 2,
    };
}

/**
 * Cap the requested zoom factor so the lens never demands more source
 * detail than the render actually has. The lens samples
 * renderedWidth * factor CSS px worth of source; the lossless ceiling is
 * naturalWidth / renderedWidth. We multiply that ceiling by ZOOM_HEADROOM
 * so a native ~= rendered source (the Artifi Stage case) still magnifies
 * ~1.5x instead of collapsing to 1x -- mild upscaling blur is an accepted
 * trade for a lens that reads as an actual zoom. Never returns below 1x.
 */
export function effectiveZoomFactor({ requested, naturalWidth, renderedWidth }) {
    if (!naturalWidth || !renderedWidth || renderedWidth <= 0) {
        return requested;
    }
    const maxLossless = (naturalWidth / renderedWidth) * ZOOM_HEADROOM;
    const capped = Math.min(requested, maxLossless);
    return Math.max(1, capped);
}

function clamp(value, min, max) {
    if (max < min) return min;
    return Math.min(Math.max(value, min), max);
}

/**
 * Given the element's client rect, the image natural dimensions, a zoom
 * factor, the lens size, and the cursor client coordinates, compute the
 * lens overlay state:
 *   - visible: is the cursor over the rendered image rectangle?
 *   - lensLeft / lensTop: top-left of the loupe, relative to the loupe's
 *     offsetParent (the CONTAINER). Clamped so the loupe stays within the
 *     rendered image rectangle. When `containerRect` is omitted it defaults
 *     to `elementRect`, i.e. element-relative coordinates.
 *   - backgroundWidth / backgroundHeight: the zoomed image size for
 *     background-size.
 *   - backgroundPosX / backgroundPosY: the background-position (negative
 *     offsets) that centers the region under the cursor in the loupe.
 *
 * The loupe is a position:absolute sibling of the <img>, so its left/top
 * apply against its offsetParent -- the .stage-preview-area container, NOT
 * the <img>. The <img> is flex-centered (and padded) inside that container,
 * so its box is offset from the container's box. All cursor->image geometry
 * runs in element/image space (below), then the final loupe top-left is
 * shifted by (elementRect - containerRect) so it lands under the cursor in
 * the container's coordinate system. Skipping that shift renders the loupe
 * up-left of the cursor by exactly the image-within-container offset.
 */
export function computeLensState({
    elementRect,
    containerRect,
    naturalWidth,
    naturalHeight,
    zoomFactor,
    lensSize,
    clientX,
    clientY,
}) {
    const hidden = { visible: false };
    if (!elementRect || !naturalWidth || !naturalHeight) return hidden;

    const imgRect = computeRenderedImageRect({
        elementWidth: elementRect.width,
        elementHeight: elementRect.height,
        naturalWidth,
        naturalHeight,
    });
    if (!imgRect) return hidden;

    // Cursor in element-relative coordinates.
    const relX = clientX - elementRect.left;
    const relY = clientY - elementRect.top;

    // Cursor relative to the rendered image rectangle (not the element box).
    const imgX = relX - imgRect.offsetX;
    const imgY = relY - imgRect.offsetY;

    // Outside the rendered image (over a letterbox bar) -> no lens.
    if (imgX < 0 || imgY < 0 || imgX > imgRect.width || imgY > imgRect.height) {
        return hidden;
    }

    const factor = zoomFactor > 0 ? zoomFactor : 1;
    const backgroundWidth = imgRect.width * factor;
    const backgroundHeight = imgRect.height * factor;

    // Fraction of the way across the rendered image (0..1).
    const fracX = imgRect.width > 0 ? imgX / imgRect.width : 0;
    const fracY = imgRect.height > 0 ? imgY / imgRect.height : 0;

    // The corresponding point in the zoomed image, then shift so that
    // point sits at the lens center -> negative background-position.
    const zoomedX = fracX * backgroundWidth;
    const zoomedY = fracY * backgroundHeight;
    const half = lensSize / 2;
    const backgroundPosX = -(zoomedX - half);
    const backgroundPosY = -(zoomedY - half);

    // Loupe top-left centered on the cursor, clamped to the rendered image
    // rectangle so it never floats over the letterbox bars or off-image.
    // These are still ELEMENT-relative (the clamp bounds are in element
    // space via imgRect.offset*).
    const lensLeftEl = clamp(
        relX - half,
        imgRect.offsetX,
        imgRect.offsetX + imgRect.width - lensSize,
    );
    const lensTopEl = clamp(
        relY - half,
        imgRect.offsetY,
        imgRect.offsetY + imgRect.height - lensSize,
    );

    // Convert element-relative -> container-relative (the loupe's
    // offsetParent). When no containerRect is supplied, the container is
    // assumed to coincide with the element (zero offset).
    const originX = containerRect ? elementRect.left - containerRect.left : 0;
    const originY = containerRect ? elementRect.top - containerRect.top : 0;
    const lensLeft = lensLeftEl + originX;
    const lensTop = lensTopEl + originY;

    return {
        visible: true,
        lensLeft,
        lensTop,
        backgroundWidth,
        backgroundHeight,
        backgroundPosX,
        backgroundPosY,
    };
}

/**
 * Vue composable. Callers pass reactive inputs:
 *   - imageEl:    ref to the <img> element (or null)
 *   - imageUrl:   ref/computed of the preview URL (background source)
 *   - enabled:    ref/computed -- lens only active when true (guards the
 *                 shimmer / fallback / empty states)
 *   - requestedZoom / lensSize: optional numeric overrides
 *
 * Returns { active, lensStyle, onMouseMove, onMouseLeave, onImageLoad,
 * reset }. lensStyle is a computed style object for the loupe element; wire
 * onMouseMove/onMouseLeave to the image, onImageLoad to its @load, and call
 * reset() when the underlying image identity changes (color switch).
 */
export function useHoverZoom({
    imageEl,
    imageUrl,
    enabled,
    requestedZoom = DEFAULT_ZOOM_FACTOR,
    lensSize = DEFAULT_LENS_SIZE,
}) {
    const active = ref(false);
    const naturalWidth = ref(0);
    const naturalHeight = ref(0);
    const lensState = ref({ visible: false });

    function reset() {
        active.value = false;
        lensState.value = { visible: false };
    }

    function readNaturalDims() {
        const el = imageEl?.value;
        if (el && el.naturalWidth && el.naturalHeight) {
            naturalWidth.value = el.naturalWidth;
            naturalHeight.value = el.naturalHeight;
        }
    }

    function onImageLoad() {
        readNaturalDims();
    }

    function onMouseMove(evt) {
        if (!enabled?.value) {
            reset();
            return;
        }
        const el = imageEl?.value;
        if (!el || typeof el.getBoundingClientRect !== "function") {
            reset();
            return;
        }
        if (!naturalWidth.value || !naturalHeight.value) {
            readNaturalDims();
        }
        const elementRect = el.getBoundingClientRect();
        // The loupe is positioned against its offsetParent -- the
        // .stage-preview-area container that flex-centers the <img>. Read
        // that container's rect so computeLensState can return
        // container-relative loupe coordinates (else the loupe renders
        // up-left of the cursor by the image-within-container offset).
        // Coupling note: this relies on the <img> and the loupe sharing an
        // offsetParent (true while the img is position:static and
        // .stage-preview-area is position:relative). If a future CSS change
        // gives the img/an ancestor position or transform, offsetParent
        // shifts and the loupe drifts again -- there's no DOM-level test to
        // catch that (the geometry tests are pure-function).
        const containerEl = el.offsetParent;
        // Fallback: when offsetParent is null (e.g. display:none, where the
        // lens isn't shown anyway) we use elementRect, which yields a zero
        // offset -- i.e. this path SILENTLY RE-CREATES the pre-fix
        // image-relative behavior. Safe today only because the lens is hidden
        // when it fires.
        const containerRect =
            containerEl &&
            typeof containerEl.getBoundingClientRect === "function"
                ? containerEl.getBoundingClientRect()
                : elementRect;
        const renderedRect = computeRenderedImageRect({
            elementWidth: elementRect.width,
            elementHeight: elementRect.height,
            naturalWidth: naturalWidth.value,
            naturalHeight: naturalHeight.value,
        });
        const factor = renderedRect
            ? effectiveZoomFactor({
                  requested: requestedZoom,
                  naturalWidth: naturalWidth.value,
                  renderedWidth: renderedRect.width,
              })
            : requestedZoom;
        const state = computeLensState({
            elementRect,
            containerRect,
            naturalWidth: naturalWidth.value,
            naturalHeight: naturalHeight.value,
            zoomFactor: factor,
            lensSize,
            clientX: evt.clientX,
            clientY: evt.clientY,
        });
        lensState.value = state;
        active.value = Boolean(state.visible);
    }

    function onMouseLeave() {
        reset();
    }

    // Hide + reset natural dims when the source image changes (color switch)
    // so a stale zoom never lingers over the new render before it loads.
    if (imageUrl) {
        watch(
            () => imageUrl.value,
            () => {
                naturalWidth.value = 0;
                naturalHeight.value = 0;
                reset();
            },
        );
    }

    const lensStyle = computed(() => {
        const s = lensState.value;
        if (!s.visible) return { display: "none" };
        return {
            display: "block",
            width: `${lensSize}px`,
            height: `${lensSize}px`,
            left: `${s.lensLeft}px`,
            top: `${s.lensTop}px`,
            backgroundImage: imageUrl?.value
                ? `url("${imageUrl.value}")`
                : "none",
            backgroundRepeat: "no-repeat",
            backgroundSize: `${s.backgroundWidth}px ${s.backgroundHeight}px`,
            backgroundPosition: `${s.backgroundPosX}px ${s.backgroundPosY}px`,
        };
    });

    return {
        active,
        lensStyle,
        onMouseMove,
        onMouseLeave,
        onImageLoad,
        reset,
    };
}
